import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Inbox, Reply, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesTab({ token, portalType, portalName }) {
  const [messages, setMessages] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/portal/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.subject.trim() || !messageForm.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/portal/messages`, {
        subject: messageForm.subject,
        message: messageForm.message,
        portal_type: portalType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent to IMCAC Admin');
      setMessageForm({ subject: '', message: '' });
      setShowCompose(false);
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter(m => m.direction === 'outgoing' && m.status === 'unread').length;

  if (loading) {
    return <div className="py-8 text-center text-slate-500">Loading messages...</div>;
  }

  return (
    <div data-testid={`${portalType}-messages-tab`}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">Messages</h3>
          <p className="text-sm text-slate-500">Communicate with IMCAC Administration</p>
        </div>
        <Button 
          onClick={() => setShowCompose(true)}
          className="bg-[#1B2A4A] text-white rounded-sm"
          data-testid="compose-message-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> New Message
        </Button>
      </div>

      {/* Compose Message Form */}
      {showCompose && (
        <Card className="mb-6 border-2 border-[#C5A572]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Send className="h-4 w-4 text-[#C5A572]" /> Compose New Message
              </span>
              <Button variant="ghost" size="sm" onClick={() => setShowCompose(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Subject *</Label>
                <Input
                  placeholder="Enter message subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="message-subject"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Message *</Label>
                <Textarea
                  placeholder="Type your message here..."
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                  required
                  rows={5}
                  className="mt-1 rounded-sm"
                  data-testid="message-body"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={sending}
                  className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                  data-testid="send-message-btn"
                >
                  <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending...' : 'Send Message'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCompose(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Messages List */}
      {messages.length === 0 ? (
        <Card className="text-center py-12">
          <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No messages yet</p>
          <p className="text-sm text-slate-400 mt-1">Start a conversation with IMCAC Admin</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {messages.map((msg) => (
            <Card 
              key={msg.id} 
              className={`cursor-pointer hover:border-[#C5A572] transition-colors ${
                selectedMessage?.id === msg.id ? 'border-[#C5A572] border-2' : ''
              } ${msg.direction === 'outgoing' && msg.status === 'unread' ? 'bg-blue-50' : ''}`}
              onClick={() => setSelectedMessage(selectedMessage?.id === msg.id ? null : msg)}
              data-testid={`message-${msg.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      msg.direction === 'incoming' ? 'bg-[#1B2A4A]' : 'bg-[#C5A572]'
                    }`}>
                      {msg.direction === 'incoming' ? (
                        <Send className="h-4 w-4 text-white" />
                      ) : (
                        <Reply className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#1B2A4A]">{msg.subject}</p>
                      <p className="text-xs text-slate-500">
                        {msg.direction === 'incoming' ? 'You → Admin' : 'Admin → You'} • {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={`rounded-sm text-xs ${
                    msg.status === 'unread' ? 'bg-blue-100 text-blue-700' :
                    msg.status === 'replied' ? 'bg-green-100 text-green-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {msg.status === 'replied' ? 'Replied' : msg.status === 'unread' ? 'New' : 'Read'}
                  </Badge>
                </div>
                
                {selectedMessage?.id === msg.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
